/**
 * Phase 2L-A99 — `/faqs` Answer Hub.
 *
 * Educational answer hub for tank & bore water testing on the
 * Sunshine Coast. People-first content, ADWG-aligned wording, with
 * a strict no-medical-claims / no-"safe to drink" content policy
 * enforced both in `/app/frontend/src/data/faqs.js` and by tests.
 *
 * FAQPage JSON-LD is emitted ONLY when the `faq_schema_enabled`
 * feature flag is on (default ON, can be flipped off centrally
 * without redeploying). The schema content is generated from the
 * SAME data array that renders the visible accordion — there's no
 * hidden-from-page schema content (Google guideline + integrity).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, BookOpen, Droplet, FlaskConical, Sun, ClipboardCheck, FileText, MapPin } from "lucide-react";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { SchemaJsonLd, faqSchema } from "@/lib/seo";

import { FAQ_CATEGORIES, FAQ_ALL_ITEMS } from "@/data/faqs";

const CATEGORY_ICON = {
  "tank-water": Droplet,
  "bore-water": FlaskConical,
  "bacteria": FlaskConical,
  "signs": Sun,
  "sampling": ClipboardCheck,
  "results": FileText,
  "packages": BookOpen,
  "town": MapPin,
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function FAQs() {
  // Feature-flag the JSON-LD so a future content audit can turn it
  // off centrally without redeploying. Reads once on mount; no
  // ongoing polling needed for a static page.
  const [schemaEnabled, setSchemaEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE_URL}/api/feature-flags/public/faq_schema_enabled`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && typeof d.enabled === "boolean") setSchemaEnabled(d.enabled);
      })
      .catch(() => {
        // Keep the default-on behaviour silently — never block render.
      });
    return () => { cancelled = true; };
  }, []);

  useSEO({
    title: "Tank & Bore Water Testing FAQs | Aware Water Testing",
    description:
      "Answers to common questions about rainwater tank testing, bore water testing, E. coli, coliforms, pH, TDS, metals, sample collection and water test reports — from Aware Water Testing, supporting customers Australia-wide.",
    url: `${SEO_DEFAULTS.baseUrl}/faqs`,
    type: "website",
  });

  const faqJsonLd = useMemo(
    () => faqSchema(FAQ_ALL_ITEMS.map(({ q, a }) => ({ q, a }))),
    [],
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      {schemaEnabled && (
        <SchemaJsonLd id="jsonld-faq-page" data={faqJsonLd} />
      )}

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-36 pb-16 lg:pb-24" data-testid="faqs-page">
        {/* Hero */}
        <header className="mb-12 lg:mb-16">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
            Answer Hub
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
            Tank &amp; Bore Water Testing FAQs
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            Clear answers about{" "}
            <Link
              to="/aware-tank-360"
              data-testid="faqs-link-tank-360"
              className="underline decoration-cyan-300 underline-offset-2 hover:text-cyan-700"
            >
              rainwater tank testing
            </Link>
            , bore water testing, bacteria, minerals, sample collection and
            what your results mean.
          </p>
          <p className="mt-3 text-base text-slate-600 max-w-3xl leading-relaxed">
            This page answers the questions Australian households on tank or
            bore water actually ask us. Results are interpreted against the
            Australian Drinking Water Guidelines (ADWG) — we report what the
            numbers say and what the typical next step looks like, so you can
            decide on inspection, filtration, UV or specialist follow-up
            alongside your installer or
            {" "}
            <Link to="/about" className="underline decoration-cyan-300 underline-offset-2 hover:text-cyan-700">
              Pristine Water Systems
            </Link>.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/book-water-test"
              data-testid="faqs-cta-book"
              className="inline-flex items-center justify-center rounded-full bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 text-sm font-semibold transition"
            >
              Book a water test →
            </Link>
            <Link
              to="/#water-tests"
              data-testid="faqs-cta-compare"
              className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white hover:border-cyan-400 hover:text-cyan-700 px-6 py-3 text-sm font-semibold text-slate-700 transition"
            >
              Compare test packages
            </Link>
          </div>
        </header>

        {/* Anchor TOC */}
        <nav
          aria-label="FAQ categories"
          data-testid="faqs-toc"
          className="mb-12 rounded-2xl bg-white ring-1 ring-slate-200 p-5"
        >
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
            Jump to a section
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            {FAQ_CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICON[c.id] || BookOpen;
              return (
                <li key={c.id}>
                  <a
                    href={`#${c.id}`}
                    data-testid={`faqs-toc-${c.id}`}
                    className="inline-flex items-center gap-2 text-slate-700 hover:text-cyan-700 transition"
                  >
                    <Icon className="w-4 h-4 text-cyan-600" />
                    {c.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Categories */}
        <div className="space-y-12 lg:space-y-16">
          {FAQ_CATEGORIES.map((cat) => (
            <CategoryBlock key={cat.id} cat={cat} />
          ))}
        </div>

        {/* Helpful links footer */}
        <section className="mt-16 lg:mt-20 rounded-3xl bg-gradient-to-br from-cyan-50 to-slate-50 ring-1 ring-cyan-100 p-6 sm:p-10">
          <h2 className="font-display text-2xl font-bold text-slate-900">
            Still have a question?
          </h2>
          <p className="mt-2 text-base text-slate-600 max-w-2xl">
            If your question isn't here, the team can help. Book a test
            online, browse the Knowledge Hub for longer-form articles, or
            see whether your suburb already has a dedicated page.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/book-water-test" data-testid="faqs-footer-book" className="inline-flex items-center rounded-full bg-cyan-600 hover:bg-cyan-700 text-white px-5 py-2.5 text-sm font-semibold transition">
              Book a water test
            </Link>
            <Link to="/knowledge-hub" data-testid="faqs-footer-kh" className="inline-flex items-center rounded-full border border-slate-300 bg-white hover:border-cyan-400 px-5 py-2.5 text-sm font-medium text-slate-700 transition">
              Visit Knowledge Hub
            </Link>
            <Link to="/blog" data-testid="faqs-footer-blog" className="inline-flex items-center rounded-full border border-slate-300 bg-white hover:border-cyan-400 px-5 py-2.5 text-sm font-medium text-slate-700 transition">
              Read the Blog
            </Link>
            <Link to="/locations" data-testid="faqs-footer-locations" className="inline-flex items-center rounded-full border border-slate-300 bg-white hover:border-cyan-400 px-5 py-2.5 text-sm font-medium text-slate-700 transition">
              Find your suburb
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function CategoryBlock({ cat }) {
  const Icon = CATEGORY_ICON[cat.id] || BookOpen;
  return (
    <section id={cat.id} data-testid={`faqs-category-${cat.id}`} className="scroll-mt-32">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5 text-cyan-600" />
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{cat.label}</h2>
      </div>
      {cat.intro && <p className="text-slate-600 mb-6 max-w-3xl leading-relaxed">{cat.intro}</p>}
      <div className="space-y-3">
        {cat.items.map((item, i) => (
          <FaqItem key={i} item={item} testidPrefix={`faqs-${cat.id}-${i}`} />
        ))}
      </div>
    </section>
  );
}

function FaqItem({ item, testidPrefix }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid={testidPrefix}
      className="group rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-cyan-200 transition open:ring-cyan-300"
    >
      <summary
        data-testid={`${testidPrefix}-summary`}
        className="cursor-pointer list-none px-5 py-4 flex items-start justify-between gap-4"
      >
        <span className="font-semibold text-slate-900 text-base sm:text-lg">{item.q}</span>
        <ChevronDown
          className={`w-5 h-5 mt-1 flex-shrink-0 text-slate-400 transition-transform ${open ? "rotate-180 text-cyan-600" : ""}`}
        />
      </summary>
      <div className="px-5 pb-5 pt-1 text-slate-600 leading-relaxed" data-testid={`${testidPrefix}-answer`}>
        {item.a}
      </div>
    </details>
  );
}
